import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import actions from './cart.actions';

import cartProducts from 'domains/cart_products';
import cartProductCreator from 'domains/cart_product_creator';

import {
  createCartOrderOption,
  deleteCartOrderOption,
  getCartOrderOptions,
} from 'domains/cart_order_options';

import type { Cart, CartSliceInitialState } from './cart.types';

const initialState: CartSliceInitialState = {
  status: 'idle',
  betterCart: null,
  cartCheck: false,
};

const cart = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    set(state, { payload }: PayloadAction<Cart>) {
      return {
        ...state,
        status: 'idle',
        betterCart: payload,
        cartCheck: true,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.check.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      // This happens when the cart check does not error, but there is no cart to fetch
      .addCase(actions.check.fulfilled, (state) => {
        return { ...state, cartCheck: true, status: 'idle' };
      })
      .addCase(actions.check.rejected, (state) => {
        return { ...state, status: 'error' };
      })
      .addCase(actions.get.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.get.fulfilled, (state, { payload }) => {
        return { ...state, betterCart: payload };
      })
      .addCase(actions.get.rejected, (state) => {
        return { ...state, status: 'error' };
      })

      .addCase(actions.create.fulfilled, (state, { payload }) => {
        return { ...state, betterCart: payload };
      })
      .addCase(actions.update.fulfilled, (state, { payload }) => {
        if (!state.betterCart) return state;

        return {
          ...state,
          betterCart: {
            ...state.betterCart,
            ...payload,
          },
        };
      })

      .addCase(cartProducts.actions.destroy.fulfilled, (state, { payload }) => {
        if (!state.betterCart) return state;

        return {
          ...state,
          betterCart: {
            ...state.betterCart,
            product_ids: state.betterCart.product_ids.filter((id) => id !== payload),
          },
        };
      })
      .addCase(createCartOrderOption.fulfilled, (state, { payload }) => {
        return { ...state, betterCart: payload };
      })
      .addCase(getCartOrderOptions.fulfilled, (state, { payload }) => {
        if (!state.betterCart) return state;

        return {
          ...state,
          betterCart: {
            ...state.betterCart,
            options: payload,
          },
        };
      })
      .addCase(deleteCartOrderOption.fulfilled, (state, { payload }) => {
        return { ...state, betterCart: payload };
      })

      .addCase(cartProductCreator.actions.addToCart.fulfilled, (state, { payload }) => {
        if (!state.betterCart) return state;

        return {
          ...state,
          betterCart: {
            ...state.betterCart,
            product_ids: [...state.betterCart.product_ids, ...payload.map((cp) => cp.id)],
          },
        };
      });
  },
});

export default cart;
